import { computed, inject, Signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { seasonFeature } from '@pool-league/frontend-web-data-season';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import {
  CompetitionStatus,
  SeasonData,
  SeasonStatus,
} from '@pool-league/shared-data-api';
import { isDefined } from '@pool-league/shared-util-misc';
import { isLoadedState } from 'ngx-http-request-state';
import { UserInfoService } from '@pool-league/frontend-shared-util-auth';
import { playersFeature } from '@pool-league/frontend-web-data-players';

export abstract class CompetitionBaseComponent<
  K extends keyof SeasonData['competitions'],
> {
  readonly CompetitionStatus = CompetitionStatus;
  readonly #store = inject(Store);
  protected readonly loadSeasonRequest$ = this.#store.select(
    seasonFeature.selectSeason,
  );
  readonly seasonId$ = this.#store
    .select(seasonFeature.selectSeasonId)
    .pipe(filter(isDefined));

  readonly loadSeasonRequest = toSignal(this.loadSeasonRequest$, {
    requireSync: true,
  });

  readonly seasonDetails = computed<SeasonData | undefined>(() => {
    const seasonRequest = this.loadSeasonRequest();
    return isLoadedState(seasonRequest) ? seasonRequest.value : undefined;
  });

  readonly me = inject(UserInfoService).getMe();
  readonly myPlayerId: Signal<string | undefined> = toSignal(
    this.#store.select(playersFeature.selectMyPlayerId),
  );

  readonly nextStatus = computed<CompetitionStatus | undefined>(() =>
    this.getNextAvailableStatus(this.seasonDetails()),
  );

  readonly canCreate = computed(() => {
    if (!this.me.isSuperUser) {
      // short-circuit tracking seasonDetails() if we're not a superuser
      return false;
    }
    const season = this.seasonDetails();
    return (
      !!season &&
      season.status !== SeasonStatus.COMPLETE &&
      (!season.competitions.eightBallRoundRobin ||
        season.competitions.eightBallRoundRobin.status ===
          CompetitionStatus.DRAFT)
    );
  });

  protected getNextAvailableStatus(
    season: Partial<SeasonData> | undefined,
  ): CompetitionStatus | undefined {
    if (!this.me.isSuperUser) {
      return undefined;
    }
    const competition = season?.competitions?.[this.getCompetitionKey()];
    if (!competition) {
      return undefined;
    }
    return this.getNextStatus(competition);
  }

  protected abstract getCompetitionKey(): K;

  protected abstract getNextStatus(
    competition: NonNullable<SeasonData['competitions'][K]>,
  ): CompetitionStatus | undefined;
}
